/**
 * DatasetComposer — Minimal first-iteration UI for composing trainable
 * datasets from platform_data_catalog entries.
 *
 * WQ-236 (Phase 4.10 Track B β1, resolves DEF-009).
 *
 * Scope is intentionally narrow for β1: catalog_union mode only — pick
 * 2+ catalog entries, give the dataset a name, click compose, see the
 * resulting row + column schema. The full 5-mode wizard (Upload Merge,
 * Catalog Join, API Enrichment, Cross-Catalog) is a follow-up β-track
 * polish WQ. β2/β3 wizards consume the dataset_id this UI produces.
 *
 * Per ADR-015 invoker-agnostic corollary: this UI hits the same API
 * endpoint that β10's pipeline-orchestrator agent will hit. No
 * "wizard-only" code paths.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface CatalogEntry {
  id: string;
  display_name?: string;
  name?: string;
  source_type?: string;
  source_id?: string;
  record_count?: number | null;
}

interface DatasetRow {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  composition_mode: string;
  status: string;
  parquet_row_count?: number | null;
  parquet_size_bytes?: number | null;
  column_schema?: Array<{ name: string; dtype: string; nullable: boolean }> | null;
  last_compose_error?: string | null;
  last_composed_at?: string | null;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  composing: '#eab308',
  ready: '#22c55e',
  stale: '#f97316',
  failed: '#ef4444',
  archived: '#6b7280',
};

const DatasetComposer: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();

  const [catalogEntries, setCatalogEntries] = useState<CatalogEntry[]>([]);
  const [datasets, setDatasets] = useState<DatasetRow[]>([]);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<string[]>([]);
  const [datasetName, setDatasetName] = useState('');
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latestResult, setLatestResult] = useState<DatasetRow | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [catalogResp, datasetsResp] = await Promise.all([
        authFetch('/api/admin/ai/data-catalog').catch(() => null),
        authFetch('/api/admin/ai/datasets').catch(() => null),
      ]);
      if (catalogResp && catalogResp.ok) {
        const data = await catalogResp.json();
        setCatalogEntries(Array.isArray(data) ? data : data?.entries || []);
      }
      if (datasetsResp && datasetsResp.ok) {
        const data = await datasetsResp.json();
        setDatasets(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading]);

  const toggleCatalogSelection = (id: string) => {
    setSelectedCatalogIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  };

  const canCompose =
    !composing &&
    datasetName.trim().length > 0 &&
    selectedCatalogIds.length >= 1;

  const handleCompose = async () => {
    if (!canCompose) return;
    setComposing(true);
    setError(null);
    try {
      const resp = await authFetch('/api/admin/ai/datasets/compose', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: datasetName.trim(),
          composition_mode: 'catalog_union',
          recipe: { catalog_entry_ids: selectedCatalogIds },
        }),
      });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => null);
        throw new Error(
          detail?.detail || `Compose failed: HTTP ${resp.status}`,
        );
      }
      const result = await resp.json();
      setLatestResult(result);
      setDatasetName('');
      setSelectedCatalogIds([]);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Compose failed');
    }
    setComposing(false);
  };

  const handleRecompose = async (id: string) => {
    setError(null);
    try {
      const resp = await authFetch(
        `/api/admin/ai/datasets/${id}/recompose`,
        { method: 'POST' },
      );
      if (!resp.ok) {
        const detail = await resp.json().catch(() => null);
        throw new Error(
          detail?.detail || `Recompose failed: HTTP ${resp.status}`,
        );
      }
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Recompose failed');
    }
  };

  if (loading && catalogEntries.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading dataset composer...
      </div>
    );
  }

  return (
    <div data-testid="dataset-composer" style={{ padding: 20 }}>
      <h2 style={{ marginTop: 0 }}>Dataset Composer (β1)</h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
        Compose a trainable dataset by unioning catalog entries. The
        resulting dataset_id can be referenced by AutoML, prediction,
        and data-quality workflows.
      </p>

      {error && (
        <div
          role="alert"
          style={{
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid #ef4444',
            color: '#ef4444',
            padding: 10,
            borderRadius: 4,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {/* Compose form */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="dataset-name"
            style={{ display: 'block', fontSize: 13, marginBottom: 4 }}
          >
            Dataset name
          </label>
          <input
            id="dataset-name"
            type="text"
            value={datasetName}
            onChange={e => setDatasetName(e.target.value)}
            placeholder="e.g., Equipment + Maintenance Union"
            style={{
              width: '100%',
              padding: 8,
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontSize: 14,
            }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            Catalog entries ({selectedCatalogIds.length} selected)
          </div>
          {catalogEntries.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
              No catalog entries available. Register data sources in the
              Data Catalog first.
            </div>
          ) : (
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 4 }}>
              {catalogEntries.map(entry => (
                <label
                  key={entry.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCatalogIds.includes(entry.id)}
                    onChange={() => toggleCatalogSelection(entry.id)}
                    style={{ marginRight: 10 }}
                  />
                  <span style={{ flex: 1 }}>
                    {entry.display_name || entry.name || entry.id}
                  </span>
                  <span
                    style={{ color: 'var(--text-secondary)', fontSize: 12 }}
                  >
                    {entry.source_type}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={handleCompose}
          disabled={!canCompose}
          style={{
            padding: '8px 16px',
            background: canCompose ? 'var(--color-primary, #2563eb)' : 'var(--border)',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            fontSize: 14,
            cursor: canCompose ? 'pointer' : 'not-allowed',
          }}
        >
          {composing ? 'Composing...' : 'Compose Dataset'}
        </button>
      </div>

      {/* Latest result */}
      {latestResult && (
        <div
          style={{
            border: '1px solid #22c55e',
            borderRadius: 6,
            padding: 12,
            marginBottom: 16,
            background: 'rgba(34, 197, 94, 0.06)',
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600 }}>
            Composed: {latestResult.name}
          </div>
          <div>Status: {latestResult.status}</div>
          <div>Rows: {latestResult.parquet_row_count ?? '—'}</div>
        </div>
      )}

      {/* Existing datasets */}
      <h3 style={{ fontSize: 16 }}>Existing Datasets ({datasets.length})</h3>
      {datasets.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          No datasets composed yet.
        </div>
      ) : (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: 6 }}>Name</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Mode</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Status</th>
              <th style={{ textAlign: 'right', padding: 6 }}>Rows</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Composed at</th>
              <th style={{ textAlign: 'left', padding: 6 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {datasets.map(d => (
              <tr key={d.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 6 }}>{d.name}</td>
                <td style={{ padding: 6 }}>{d.composition_mode}</td>
                <td style={{ padding: 6 }}>
                  <span
                    style={{
                      color: STATUS_COLORS[d.status] || '#6b7280',
                      fontWeight: 600,
                    }}
                  >
                    {d.status}
                  </span>
                </td>
                <td style={{ padding: 6, textAlign: 'right' }}>
                  {d.parquet_row_count ?? '—'}
                </td>
                <td style={{ padding: 6 }}>
                  {d.last_composed_at
                    ? new Date(d.last_composed_at).toLocaleString()
                    : '—'}
                </td>
                <td style={{ padding: 6 }}>
                  <button
                    onClick={() => handleRecompose(d.id)}
                    style={{
                      padding: '4px 8px',
                      fontSize: 12,
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    Recompose
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DatasetComposer;
